<script setup lang="ts">
    import { reactive, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import InputRadio from '@shared/components/InputRadio.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { createRole, updateRole, type FeatureRequirement, type FeatureSummary, type Role, type RolePermission } from './Roles.access';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';

    const Permission = { None: 0, Read: 1, Write: 2, Execute: 4 };

    const props = defineProps<{
        show: boolean;
        role: Role | null;
        features: FeatureSummary[];
        featureRequirements: FeatureRequirement[];
    }>();

    const emit = defineEmits<{
        close: [];
        saved: [];
    }>();

    interface EditablePermission {
        Feature: number;
        Label: string;
        Permission: number;
        RequiredLevel: number;
        Executable: boolean;
        HideRead: boolean;
    }

    const state = reactive<{
        name: string;
        internalDescription: string;
        permissions: EditablePermission[];
        isSaving: boolean;
    }>({
        name: '',
        internalDescription: '',
        permissions: [],
        isSaving: false,
    });

    function buildPermissions(existing: RolePermission[]): EditablePermission[] {
        return props.features.map((feature) => {
            const match = existing.find((p) => p.Feature === feature.Value);

            return {
                Feature: feature.Value,
                Label: featureLabel(feature),
                Permission: match ? match.Permission : Permission.None,
                RequiredLevel: Permission.None,
                Executable: feature.Executable,
                HideRead: feature.HideRead,
            };
        });
    }

    function enforceRequirements(): void {
        state.permissions.forEach((permission) => (permission.RequiredLevel = Permission.None));

        state.permissions.forEach((permission) => {
            props.featureRequirements.forEach((requirement) => {
                if (requirement.Feature !== permission.Feature || requirement.Permission > permission.Permission) return;

                const target = state.permissions.find((p) => p.Feature === requirement.RequiredFeature);
                if (!target) return;

                if (target.Permission < requirement.RequiredPermission) target.Permission = requirement.RequiredPermission;
                if (target.RequiredLevel < requirement.RequiredPermission) target.RequiredLevel = requirement.RequiredPermission;
            });
        });
    }

    watch(
        () => props.show,
        (show) => {
            if (!show) return;

            state.name = props.role?.Name ?? '';
            state.internalDescription = props.role?.InternalDescription ?? '';
            state.permissions = buildPermissions(props.role?.Permissions ?? []);
            if (props.role) enforceRequirements();
        }
    );

    function featureLabel(feature: FeatureSummary): string {
        return ($Resources as any)['NewRoleFeatureList' + feature.Name] || feature.Name;
    }

    function close(): void {
        emit('close');
    }

    async function save(): Promise<void> {
        if (state.isSaving || !state.name) return;

        state.isSaving = true;

        const payload = {
            RoleId: props.role?.RoleId,
            Name: state.name,
            InternalDescription: state.internalDescription,
            Permissions: state.permissions.map((p) => ({ Feature: p.Feature, Permission: p.Permission })),
        };

        try {
            if (props.role) {
                await updateRole(props.role.RoleId, payload);
            } else {
                await createRole(payload);
            }

            bsAlert({ type: AlertTypes.Success, content: props.role ? $Resources.AlertRoleEditedBody : $Resources.AlertRoleCreatedBody });
            emit('saved');
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isSaving = false;
        }
    }
</script>

<template>
    <Modal :title="props.role ? $Resources.RoleEditHeading : $Resources.RoleCreateNewHeading" :show="props.show" @close="close" hideHeader hideFooter>
        <template #content>
            <button type="button" class="btn btn-default btn-modal-close p-absolute round" @click="close">
                <NavIcons icon="close"></NavIcons>
                <span class="sr-only">{{ $Resources.CloseButton }}</span>
            </button>

            <h3 class="text-center">{{ props.role ? $Resources.RoleEditHeading : $Resources.RoleCreateNewHeading }}</h3>

            <p class="text-center m-xl-b">{{ props.role ? $Resources.RoleEditBody : $Resources.RoleCreateNewLabel }}</p>

            <FormInput class="form-group" id="roleName" :label="$Resources.RoleNameLabel" v-model="state.name" required></FormInput>

            <FormInput class="form-group" id="roleDescription" :label="$Resources.RoleDescriptionLabel" v-model="state.internalDescription"></FormInput>

            <h3 class="h5 m-xl-t">{{ $Resources.RolePermissionsLabel }}:</h3>

            <div class="table-responsive">
                <table class="table table-hover table-roles td-align-middle">
                    <tbody>
                        <tr v-for="permission in state.permissions" :key="permission.Feature">
                            <td class="fw-600">
                                <p>{{ permission.Label }}</p>
                            </td>
                            <td class="autofit text-nowrap">
                                <InputRadio
                                    v-if="permission.RequiredLevel <= Permission.None"
                                    :name="'permission' + permission.Feature"
                                    :value="Permission.None"
                                    :checked="permission.Permission === Permission.None"
                                    @update:checked="
                                        () => {
                                            permission.Permission = Permission.None;
                                            enforceRequirements();
                                        }
                                    ">
                                    <template #label>{{ $Resources.RolePermissionsNoneLabel }}</template>
                                </InputRadio>
                            </td>
                            <td class="autofit text-nowrap">
                                <InputRadio
                                    v-if="permission.RequiredLevel <= Permission.Read && !permission.HideRead"
                                    :name="'permission' + permission.Feature"
                                    :value="Permission.Read"
                                    :checked="permission.Permission === Permission.Read"
                                    @update:checked="
                                        () => {
                                            permission.Permission = Permission.Read;
                                            enforceRequirements();
                                        }
                                    ">
                                    <template #label>{{ $Resources.RolePermissionsViewLabel }}</template>
                                </InputRadio>
                            </td>
                            <td class="autofit text-nowrap">
                                <InputRadio
                                    v-if="permission.RequiredLevel <= Permission.Write"
                                    :name="'permission' + permission.Feature"
                                    :value="Permission.Write"
                                    :checked="permission.Permission === Permission.Write"
                                    @update:checked="
                                        () => {
                                            permission.Permission = Permission.Write;
                                            enforceRequirements();
                                        }
                                    ">
                                    <template #label>{{ $Resources.RolePermissionsEditLabel }}</template>
                                </InputRadio>
                            </td>
                            <td class="autofit text-nowrap">
                                <InputRadio
                                    v-if="permission.Executable && permission.RequiredLevel <= Permission.Execute"
                                    :name="'permission' + permission.Feature"
                                    :value="Permission.Execute"
                                    :checked="permission.Permission === Permission.Execute"
                                    @update:checked="
                                        () => {
                                            permission.Permission = Permission.Execute;
                                            enforceRequirements();
                                        }
                                    ">
                                    <template #label>{{ $Resources.RolePermissionsActivateLabel }} *</template>
                                </InputRadio>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="small m-lg-b">* {{ $Resources.RolePermissionsSendOrPublish }}</p>

            <div class="btn-container form-group p-sm-t">
                <button
                    class="btn btn-primary btn-block btn-lg"
                    :class="state.isSaving && 'is-busy'"
                    type="button"
                    :disabled="!state.name || state.isSaving"
                    @click="save">
                    {{ props.role ? $Resources.RolePermissionsSaveChanges : $Resources.RolePermissionsCreateRole }}
                </button>
            </div>
        </template>
    </Modal>
</template>

<style>
    .table-roles td {
        padding: var(--ug-spacing-sm) var(--ug-spacing-lg) 0 var(--ug-spacing-xxs) !important;
    }
</style>

